import { uniqueId } from 'lodash';
import { reactive } from 'vue';
export const ConfirmResult = {
  OK: 'OK',
  CANCEL: 'CANCEL',
  DISMISS: 'DISMISS',
  CLOSE: 'CLOSE',
  ERROR: 'ERROR',
};

export class ConfirmDialogConfig {
  constructor(config = {}) {
    this.id = config.id ?? `ag-dialog-confirm-${uniqueId()}`;
    this.isOpen = config.isOpen ?? true;
    this.persistent = config.persistent ?? false;
    this.maxWidth = config.maxWidth ?? '550px';
    this.width = config.width ?? '550px';
    this.scrollable = config.scrollable ?? false;
    this.cancelOnClose = config.cancelOnClose ?? false;
    this.cancelOnDismiss = config.cancelOnDismiss ?? false;
    this.title = config.title ?? 'Atenção!';
    this.message = config.message ?? 'Você tem certeza de que deseja prosseguir?';
    this.ok = {
      type: (config.ok && config.ok.type) ?? 'primary',
      label: (config.ok && config.ok.label) ?? 'Confirmar',
      action: (config.ok && config.ok.action) ?? (() => true),
    };
    this.cancel = {
      type: (config.cancel && config.cancel.type) ?? 'tertiary',
      label: (config.cancel && config.cancel.label) ?? 'Cancelar',
      action: (config.cancel && config.cancel.action) ?? (() => false),
    };
    this.loading = config.loading ?? { ok: false, cancel: false };
  }
}

export class ConfirmManager {
  constructor() {
    this.dialogs = reactive([]);
  }

  confirm(config) {
    let onOk, onCancel, onDismiss, onClose;
    let dialogRef = null;
    const promise = new Promise((resolve, reject) => {
      const dialog = new ConfirmDialogConfig({ ...config });
      dialogRef = dialog;
      dialog._resolve = async (result) => {
        let handlerResult;
        let enumResult = result;
        try {
          if (result === true && onOk) {
            handlerResult = onOk(ConfirmResult.OK);
            enumResult = ConfirmResult.OK;
          } else if (result === false && onCancel) {
            handlerResult = onCancel(ConfirmResult.CANCEL);
            enumResult = ConfirmResult.CANCEL;
          } else if (result === 'dismiss') {
            enumResult = ConfirmResult.DISMISS;
            if (onDismiss) handlerResult = onDismiss(ConfirmResult.DISMISS);
          } else if (result === 'close') {
            enumResult = ConfirmResult.CLOSE;
            if (onClose) handlerResult = onClose(ConfirmResult.CLOSE);
          } else if (result === true && dialog.ok && dialog.ok.action) {
            handlerResult = dialog.ok.action(ConfirmResult.OK);
            enumResult = ConfirmResult.OK;
          } else if (result === false && dialog.cancel && dialog.cancel.action) {
            handlerResult = dialog.cancel.action(ConfirmResult.CANCEL);
            enumResult = ConfirmResult.CANCEL;
          }
          if (handlerResult instanceof Promise) {
            await handlerResult;
          }
          resolve(enumResult);
        } catch (e) {
          reject(e);
        }
      };
      this.dialogs.push(dialog);
    });
    return {
      onOk(fn) {
        onOk = fn;
        if (dialogRef) dialogRef.ok.action = fn;
        return this;
      },
      onCancel(fn) {
        onCancel = fn;
        if (dialogRef) dialogRef.cancel.action = fn;
        return this;
      },
      onDismiss(fn) {
        onDismiss = fn;
        return this;
      },
      onClose(fn) {
        onClose = fn;
        return this;
      },
      then: (...args) => promise.then(...args),
      catch: (...args) => promise.catch(...args),
      finally: (...args) => promise.finally(...args),
    };
  }

  /**
   * Resolves the dialog with the given type. Handles cancelOnClose and cancelOnDismiss logic:
   * - If type is 'close' and dialog.cancelOnClose, treat as 'cancel'.
   * - If type is 'dismiss' and dialog.cancelOnDismiss, treat as 'cancel'.
   * Otherwise, resolve as normal.
   */
  async resolve(id, type) {
    const index = this.dialogs.findIndex((d) => d.id === id);
    if (index === -1) return;

    const dialog = this.dialogs[index];

    // Handle cancelOnClose and cancelOnDismiss logic
    if ((type === 'close' && dialog.cancelOnClose) || (type === 'dismiss' && dialog.cancelOnDismiss)) {
      // Only trigger cancel logic, not close/dismiss hooks
      this.dialogs.splice(index, 1, { ...dialog, loading: { ...dialog.loading, cancel: true } });
      try {
        await dialog._resolve?.(false); // false triggers cancel logic
      } catch (e) {
        dialog._resolve?.(ConfirmResult.ERROR);
      } finally {
        this.dialogs.splice(index, 1, { ...dialog, loading: { ok: false, cancel: false }, isOpen: false });
      }
      return;
    }

    if (type === 'close' || type === 'dismiss') {
      await dialog._resolve?.(type);
      this.dialogs.splice(index, 1, { ...dialog, isOpen: false });
      return;
    }

    this.dialogs.splice(index, 1, {
      ...dialog,
      loading: { ...dialog.loading, [type]: true },
    });

    try {
      await dialog._resolve?.(type === 'ok');
    } catch (e) {
      dialog._resolve?.(ConfirmResult.ERROR);
    } finally {
      this.dialogs.splice(index, 1, { ...dialog, loading: { ok: false, cancel: false }, isOpen: false });
    }
  }

  remove(id) {
    const index = this.dialogs.findIndex((d) => d.id === id);
    if (index !== -1) this.dialogs.splice(index, 1);
  }
}

export const confirmManager = new ConfirmManager();
export const $confirm = (...args) => confirmManager.confirm(...args);

export default {
  install(app) {
    app.config.globalProperties.$confirm = $confirm;
    app.provide('confirmManager', confirmManager);
  },
};
